import {CompositeDeviceProvide, DeviceProvide} from "@hybird/core/device/provide";
import {DeviceEvent, DeviceProtocol} from "@hybird/core/device/protocol"

import {CompositeDeviceConnectProvide, DeviceConnectProvide} from "@hybird/core/device/connect";
import {MqttDeviceConnectProvide, MqttSend} from "@hybird/device/protocol/mqtt";
import {WebscoketDeviceConnectProvide} from "@hybird/device/protocol/websocket";
import {BleDeviceConnectProvide} from "@hybird/device/protocol/ble";
import {createWebBluetoothDevice} from "@hybird/device/connect/webBluetoothDevice";
import {Config, DeviceInfo, setConfig} from "./config";

import {ADeviceProvide} from "./terminal/a";
import {AplusV2DeviceProvide} from "@hybird/device/terminal/aplusv2";
import {RemoteBeginSealInterceptor} from "@hybird/device/interceptor/beginSeal/remoteBeginSeal";
import {DeviceMethodInterceptor} from "@hybird/device/interceptor/beginSeal/core";
import {WebError} from "@hybird/core/error";
import {deviceError} from "@hybird/device/deviceError";
import {BeforeSealPhotoBeginSealInterceptor} from "@hybird/device/interceptor/beginSeal/beforeSealPhotoBeginSeal";
import {OcrBeginSealInterceptor} from "@hybird/device/interceptor/beginSeal/ocrBeginSeal";
import {QrcodeBeginSealInterceptor} from "@hybird/device/interceptor/beginSeal/qrcodeBeginSeal";
import {FirmwareUpgradeOperationInterceptor} from "@hybird/device/interceptor/operation/FirmwareUpgrade";
import {SimpleDeviceProvide} from "@hybird/device/terminal/simple";
import {AplusJgV2DeviceProvide} from "@hybird/device/terminal/aplusJgv2";
import {SerialDeviceConnectProvide} from "@hybird/device/protocol/serial";
import {createWebSerialDevice} from "@hybird/device/connect/webSerialDevice";
import {EquipmentRecBeginSealInterceptor} from "@hybird/device/interceptor/beginSeal/equipmentRecBeginSeal";
import {AMapLocationBeginSealInterceptor, getGeolocation} from "@hybird/device/interceptor/beginSeal/amapBeginSeal";
import {cameraRemoteLock} from "@hybird/device/protocol/extension/cameraRemoteLock";
import {beginSealReady} from "@hybird/device/protocol/extension/beginSealReady";
import {DirectDeviceConnectProvide, DirectDeviceProvide} from "@hybird/device/protocol/direct";
import {
  VisibilityChangeBeginSealDeviceMethodInterceptor,
  VisibilityChangeDeviceProtocolDeviceMethodInterceptor,
  VisibilityChangeOperationDeviceMethodInterceptor
} from "@hybird/device/interceptor/beginSeal/visibilityChangeBeginSeal";
import {ContinuousAutoSealStretchInterceptor} from "@hybird/device/interceptor/beginSeal/continuousBeginSeal";
import {
  NoSleepBeginSealInterceptor,
  NoSleepOperationDeviceMethodInterceptor
} from "@hybird/device/interceptor/beginSeal/noSleepServiceBeginSeal";
import {ASmartV2DeviceProvide} from "@hybird/device/terminal/asmartv2";
import {
  AdbDeviceMethodInterceptor,
  AdbOperationDeviceMethodInterceptor
} from "@hybird/device/interceptor/adbInterceptor";

const mqttDeviceConnectProvide = new MqttDeviceConnectProvide();
const webscoketDeviceConnectProvide = new WebscoketDeviceConnectProvide();

/**
 *
 * @type {(DeviceConnectProvide)[]}
 */
const deviceConnectProvides = [webscoketDeviceConnectProvide, mqttDeviceConnectProvide];

const deviceConnectProvide = new CompositeDeviceConnectProvide({
  deviceConnectProvides
});

/**
 *
 * @type {(DeviceConnectProvide)[]}
 */
const networkDeviceConnectProvides = [webscoketDeviceConnectProvide, mqttDeviceConnectProvide];

const networkConnectProvide = new CompositeDeviceConnectProvide({
  deviceConnectProvides: networkDeviceConnectProvides
});

//web ble
const bleDeviceConnectProvide = new BleDeviceConnectProvide(createWebBluetoothDevice, networkConnectProvide);
deviceConnectProvides.push(bleDeviceConnectProvide);

//web serial
const serial = new SerialDeviceConnectProvide(createWebSerialDevice, networkConnectProvide);
deviceConnectProvides.push(serial);

class Device {

  /**
   *
   * @type {{String, DeviceProtocol}}
   * @private
   */
  static _device = {};
  static _propertyDevice = {};

  static get device() {
    return Object.assign({}, Device._device);
  }

  static getByNumber(number) {
    return Device._device[number];
  }

  /**
   * @type{DeviceProvide}
   */
  static defaultDeviceProvide = new CompositeDeviceProvide({
    deviceProvides: [
      AplusV2DeviceProvide, ADeviceProvide, SimpleDeviceProvide, AplusJgV2DeviceProvide, DirectDeviceProvide, ASmartV2DeviceProvide
    ],
    deviceConnectProvide: deviceConnectProvide
  });

  /**
   *
   * @type {[DeviceMethodInterceptor | DeviceInterceptor]}
   */
  static interceptors = [
    AMapLocationBeginSealInterceptor,
    BeforeSealPhotoBeginSealInterceptor,
    RemoteBeginSealInterceptor,
    OcrBeginSealInterceptor,
    QrcodeBeginSealInterceptor,
    FirmwareUpgradeOperationInterceptor,
    EquipmentRecBeginSealInterceptor,
    VisibilityChangeDeviceProtocolDeviceMethodInterceptor,
    ContinuousAutoSealStretchInterceptor,
    // NoSleepBeginSealInterceptor,
    // NoSleepOperationDeviceMethodInterceptor
    AdbDeviceMethodInterceptor,
    AdbOperationDeviceMethodInterceptor
  ];

  deviceProvide;
  /**
   * @type{DeviceProtocol}
   */
  deviceProtocol;

  /**
   * 属性上报的监听, 不能调用beginSeal等操作, 会乱
   * @param deviceProvide
   * @param deviceInfo
   * @param config
   * @return {Promise<*>}
   */
  static async property({
                          deviceProvide = Device.defaultDeviceProvide,
                          deviceInfo,
                          config
                        }) {
    config = setConfig(deviceInfo, config);

    await networkConnectProvide.connect({config});
    let deviceProtocol = Device._propertyDevice[deviceInfo.number];

    if (deviceProtocol == null) {
      deviceInfo.property = true;
      const deviceProtocolClass = deviceProvide.getDeviceProtocol(deviceInfo)
      if (deviceProtocolClass) {
        deviceProtocol = Device._propertyDevice[deviceInfo.number] = new deviceProtocolClass(networkConnectProvide, deviceInfo, config);
      }
    }

    return deviceProtocol;
  }

  async _connect({
                   deviceProvide = Device.defaultDeviceProvide,
                   deviceInfo,
                   onConnect,
                   onConnected,
                   onDisconnected,
                   onDisconnect,
                   onError,
                   success, complete, message, fail, timeout,
                   interceptors = Device.interceptors,
                   request
                 } = {}, config) {
    config = setConfig(deviceInfo, config);
    const deviceProtocol = deviceProvide.getDeviceProtocol(deviceInfo)

    if (this.deviceProtocol) {
      if (this.deviceProtocol.connected) {
        if (deviceProtocol === this.deviceProtocol) {
          return this.deviceProtocol;
        }
      }
    }

    if (!(this.deviceProtocol instanceof deviceProtocol) || !this.deviceProtocol) {
      this.deviceProtocol = new (deviceProvide.getDeviceProtocol(deviceInfo))(Device.defaultDeviceProvide, deviceInfo, {
        onConnect, onConnected, onError, onDisconnected, onDisconnect, request
      });
      //对拦截器进行排序
      interceptors.map(interceptor => {
        return new interceptor(this.deviceProtocol);
      }).sort((o, n) =>
              (o.order ? o.order : 50) - (n.order ? n.order : 50)
      ).forEach(interceptor => {
        interceptor.createProxy();
      })
    }

    return await this.deviceProtocol.connect({success, complete, message, fail, timeout, config});
  }


  /**
   *
   * @param deviceProvide 设备提供者
   * @param deviceInfo{DeviceInfo} 设备连接信息
   * @param onConnect{function}
   * @param onConnected{function}
   * @param onError
   * @param onDisconnected
   * @param onDisconnect
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @param interceptors{[DeviceMethodInterceptor | DeviceInterceptor]} 拦截器
   * @param deviceProtocolRequest
   * @param config{Config} 配置文件
   * @return {Promise<DeviceProtocol>} 设备协议
   */
  static async connect({
                         deviceProvide,
                         deviceInfo,
                         onConnect, onConnected, onError,
                         onDisconnected,
                         onDisconnect,
                         success, complete, message, fail, timeout,
                         interceptors,
                         request
                       } = {},
                       config = {}) {
    let device = Device._device[deviceInfo.number];
    if (deviceInfo.number) {
      if (device && device.deviceProtocol) {
        device.deviceProtocol.deviceInfo = deviceInfo;
      } else {
        device = Device._device[deviceInfo.number] = new Device();
      }
    } else {
      throw WebError.code(deviceError.CONNECT_ILLEGAL_ARGUMENT, {
        msg: "deviceInfo number 不允许为null"
      });
    }

    return device._connect({
      deviceInfo,
      deviceProvide,
      onConnect,
      onConnected,
      onError,
      onDisconnected,
      onDisconnect,
      success,
      complete,
      message,
      fail,
      timeout,
      interceptors,
      request
    }, config);
  }

  static async close() {
    await Device._close(Device._device);
    await Device._close(Device._propertyDevice);

    await deviceConnectProvide.close(true);
    Device._device = [];
    Device._propertyDevice = [];
  }

  static async _close(devices) {
    for (const i in devices) {
      const device = devices[i];

      const deviceProtocol = device instanceof Device ? device.deviceProtocol : device;
      await deviceProtocol.disconnect().catch(e => {
        console.warn("disconnect 异常", e)
      });

      await deviceProtocol.close().catch(e => {
        console.warn("close 异常", e)
      });
    }
  }
}

function createDeviceInfo({
                            loginData,
                            deviceInfo
                          }) {
  const info = new DeviceInfo();

  Object.assign(info, deviceInfo);

  if (loginData) {
    info.sessionId = loginData.sessionId;
    info.userId = loginData.userId;
    info.staffId = loginData.result.staffId;
    info.userName = loginData.userName;
  }
  return info;
}

/**
 *
 * @param deviceProtocol{DeviceProtocol}
 * @param e
 * @param count
 */
async function changeConnect(deviceProtocol, e, count = 0) {
  if (count > 3) {
    throw WebError.code(deviceError.DEVICE_USE_NOT_CONNECT);
  }

  if (e.data.change === 2) {
    if (!(await deviceProtocol.deviceStatus())) {
      throw e;
    }
  }

  if (deviceProtocol.connected) {
    await deviceProtocol.disconnect({});
  }

  const deviceInfo = deviceProtocol.deviceInfo;
  deviceInfo.connectType = e.data.change;
  try {
    await Device.connect({deviceInfo});
  } catch (error) {
    if (deviceError.isError(error, deviceError.DEVICE_USE_NOT_CONNECT)) {
      await delay(5);
      return changeConnect(deviceProtocol, e, ++count);
    }

    throw e;
  }
}


async function delay(seconds = 0) {
  return new Promise(resolve => setTimeout(resolve, seconds * 1000));
}

export default Device.connect;

export {
  DeviceProtocol,
  Device,
  DeviceInfo,
  CompositeDeviceProvide,
  DeviceProvide,
  DeviceConnectProvide,
  deviceConnectProvide,
  networkConnectProvide,
  networkDeviceConnectProvides,
  deviceConnectProvides,
  createDeviceInfo,
  beginSealReady,
  cameraRemoteLock,
  DeviceEvent,
  getGeolocation,
  changeConnect
};